/**
 * RecordingList Component
 *
 * Left sidebar listing saved step sequences (recordings).
 * Fetches sequences on mount and displays domain, task, step count, and date.
 */

import React, { useEffect, useState } from 'react';
import { playgroundService } from '../../services/playgroundApi';
import { StepSequence } from '../../types/playground';

interface RecordingListProps {
  selectedId: string | null;
  onSelect: (id: string) => void;
}

export const RecordingList: React.FC<RecordingListProps> = ({
  selectedId,
  onSelect,
}) => {
  const [sequences, setSequences] = useState<StepSequence[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    playgroundService
      .getStepSequences()
      .then((result) => {
        if (!cancelled) {
          setSequences(result.sequences);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load recordings');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const formatDate = (dateStr: string): string => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `${diffDay}d ago`;
    return d.toLocaleDateString();
  };

  if (isLoading) {
    return (
      <div className="w-64 border-r border-gray-200 overflow-y-auto flex items-center justify-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-64 border-r border-gray-200 overflow-y-auto p-4">
        <p className="text-xs text-red-500">{error}</p>
      </div>
    );
  }

  if (sequences.length === 0) {
    return (
      <div className="w-64 border-r border-gray-200 overflow-y-auto flex items-center justify-center">
        <p className="text-sm text-gray-400">No recordings yet</p>
      </div>
    );
  }

  return (
    <div className="w-64 border-r border-gray-200 overflow-y-auto bg-white">
      <div className="px-3 py-2 border-b border-gray-100">
        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Recordings ({sequences.length})
        </h2>
      </div>
      <div className="divide-y divide-gray-100">
        {sequences.map((seq) => {
          const isSelected = selectedId === seq.sequence_id;
          return (
            <button
              key={seq.sequence_id}
              onClick={() => onSelect(seq.sequence_id)}
              className={`w-full text-left px-3 py-2.5 transition-colors ${
                isSelected
                  ? 'bg-blue-50 border-l-2 border-blue-500'
                  : 'border-l-2 border-transparent hover:bg-gray-50'
              }`}
            >
              <p className="text-xs font-semibold text-gray-900 truncate">
                {seq.domain}
              </p>
              <p className="text-xs text-gray-500 truncate mt-0.5">
                {seq.name}
              </p>
              <p className="text-[10px] text-gray-400 mt-0.5">
                {seq.step_count} steps · {formatDate(seq.created_at)}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
};
